/**
 * Admin Org Tree — Track 5.2.
 *
 * Two-pane editor for cdm_orgs:
 *   - Left: hierarchical tree, click to select, click-edit to reparent
 *           (drop target appears next to each node when in edit mode).
 *   - Right: detail form for the selected node + ring-fence button.
 *
 * Schema cdm_orgs already exists from Phase 1; this is the admin surface
 * that finally lets a human curate it. Backend: /api/cdm/orgs*.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2, GitBranch, Lock, RefreshCw, ChevronLeft, ChevronRight,
  ChevronDown, Plus, Trash2, X, AlertTriangle, Save, Move, ShieldOff,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

// ── Types ───────────────────────────────────────────────────────────────────

interface Org {
  id: string
  parent_id: string | null
  name: string
  kind: 'group' | 'entity' | 'business_unit' | 'ring_fenced' | string
  jurisdiction: string | null
  regulatory_zone: string | null
  data_residency: string
  isolation_mode: 'shared' | 'logical' | 'physical' | 'ring_fenced' | string
  settings: Record<string, any>
  created_at: string | null
}

interface TreeNode extends Org {
  children: TreeNode[]
  depth: number
}

const KINDS = ['group', 'entity', 'business_unit', 'ring_fenced'] as const
const ISOLATION_MODES = ['shared', 'logical', 'physical', 'ring_fenced'] as const
const RESIDENCIES = ['EEA', 'UK', 'US', 'APAC', 'CA', 'AU', 'JP', 'CH'] as const

const FLAG_BY_RESIDENCY: Record<string, string> = {
  EEA: 'EU',
  UK:  'UK',
  US:  'US',
  APAC: 'APAC',
  CA:  'CA',
  AU:  'AU',
  JP:  'JP',
  CH:  'CH',
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function buildTree(rows: Org[]): TreeNode[] {
  const byParent: Record<string, Org[]> = {}
  for (const o of rows) {
    const k = o.parent_id || '__root__'
    byParent[k] = byParent[k] || []
    byParent[k].push(o)
  }
  const expand = (o: Org, depth: number): TreeNode => ({
    ...o,
    depth,
    children: (byParent[o.id] || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => expand(c, depth + 1)),
  })
  return (byParent.__root__ || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(o => expand(o, 0))
}

function isolationBadgeClass(mode: string): string {
  if (mode === 'ring_fenced') return 'bg-rose/20 text-rose-light border-rose/40'
  if (mode === 'physical')    return 'bg-amber/20 text-amber-light border-amber/40'
  if (mode === 'logical')     return 'bg-cyan/20 text-cyan-light border-cyan/40'
  return 'bg-emerald/20 text-emerald-light border-emerald/40'
}

function kindBadgeClass(kind: string): string {
  if (kind === 'group')         return 'bg-blue/20 text-blue-light'
  if (kind === 'entity')        return 'bg-purple/20 text-purple-light'
  if (kind === 'business_unit') return 'bg-surface-raised text-text-secondary'
  if (kind === 'ring_fenced')   return 'bg-rose/20 text-rose-light'
  return 'bg-surface-raised text-text-muted'
}

// ── Form state for the right pane ───────────────────────────────────────────

interface FormState {
  name: string
  kind: string
  jurisdiction: string
  regulatory_zone: string
  data_residency: string
  isolation_mode: string
  settings_json: string
}

const FRESH_FORM: FormState = {
  name: '',
  kind: 'business_unit',
  jurisdiction: '',
  regulatory_zone: '',
  data_residency: 'EEA',
  isolation_mode: 'shared',
  settings_json: '{}',
}

function orgToForm(o: Org): FormState {
  return {
    name:            o.name,
    kind:            o.kind,
    jurisdiction:    o.jurisdiction || '',
    regulatory_zone: o.regulatory_zone || '',
    data_residency:  o.data_residency,
    isolation_mode:  o.isolation_mode,
    settings_json:   JSON.stringify(o.settings || {}, null, 2),
  }
}

// ── Tree rendering ──────────────────────────────────────────────────────────

interface TreeRowProps {
  node: TreeNode
  selectedId: string | null
  collapsed: Set<string>
  onSelect: (id: string) => void
  onToggle: (id: string) => void
  reparentMode: boolean
  reparentSourceId: string | null
  onReparentTarget: (id: string | null) => void
}

function TreeRow({
  node, selectedId, collapsed, onSelect, onToggle,
  reparentMode, reparentSourceId, onReparentTarget,
}: TreeRowProps) {
  const isOpen = !collapsed.has(node.id)
  const isSelected = selectedId === node.id
  const hasKids = node.children.length > 0
  const isReparentSelf = reparentSourceId === node.id
  const canBeTarget = reparentMode && !isReparentSelf
  const flag = FLAG_BY_RESIDENCY[node.data_residency] || node.data_residency

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-1.5 py-1 px-1 rounded cursor-pointer text-xs',
          isSelected && 'bg-blue/20 ring-1 ring-blue/40',
          !isSelected && 'hover:bg-surface-raised/40',
          canBeTarget && 'hover:ring-1 hover:ring-amber/60',
          isReparentSelf && 'opacity-50 italic',
        )}
        style={{ paddingLeft: `${node.depth * 12 + 4}px` }}
        onClick={() => {
          if (canBeTarget) {
            onReparentTarget(node.id)
          } else {
            onSelect(node.id)
          }
        }}
      >
        {hasKids ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(node.id) }}
            className="text-text-muted hover:text-text-primary"
            aria-label={isOpen ? 'collapse' : 'expand'}
          >
            {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          </button>
        ) : (
          <span className="w-[11px] inline-block" />
        )}
        {node.isolation_mode === 'ring_fenced' ? (
          <Lock size={11} className="text-rose-light flex-shrink-0" />
        ) : (
          <Building2 size={11} className="text-text-muted flex-shrink-0" />
        )}
        <span className="font-mono truncate flex-1 min-w-0">{node.name}</span>
        <span className={clsx('text-[9px] px-1 rounded font-mono uppercase', kindBadgeClass(node.kind))}>
          {node.kind === 'business_unit' ? 'bu' : node.kind === 'ring_fenced' ? 'rf' : node.kind}
        </span>
        <span className={clsx(
          'text-[9px] px-1 rounded font-mono border uppercase',
          isolationBadgeClass(node.isolation_mode),
        )} title={`isolation: ${node.isolation_mode}`}>
          {node.isolation_mode === 'ring_fenced' ? 'fenced'
            : node.isolation_mode === 'business_unit' ? 'bu'
            : node.isolation_mode}
        </span>
        <span className="text-[9px] text-text-muted font-mono" title={`data residency: ${node.data_residency}`}>
          {flag}
        </span>
      </div>
      {isOpen && hasKids && (
        <div>
          {node.children.map(c => (
            <TreeRow
              key={c.id}
              node={c}
              selectedId={selectedId}
              collapsed={collapsed}
              onSelect={onSelect}
              onToggle={onToggle}
              reparentMode={reparentMode}
              reparentSourceId={reparentSourceId}
              onReparentTarget={onReparentTarget}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────

export default function AdminOrgsPage() {
  const [orgs, setOrgs] = useState<Org[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(FRESH_FORM)
  const [dirty, setDirty] = useState(false)

  // Reparent (click-to-edit, since we don't depend on react-dnd here)
  const [reparentMode, setReparentMode] = useState(false)
  const [reparentSourceId, setReparentSourceId] = useState<string | null>(null)

  // Ring-fence confirm modal
  const [fencing, setFencing] = useState(false)
  const [fenceConfirm, setFenceConfirm] = useState('')

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/orgs')
      .then(r => setOrgs(r.data?.orgs || []))
      .catch(e => setMessage(`Load failed: ${e.response?.data?.detail || e.message}`))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const tree = useMemo(() => buildTree(orgs), [orgs])
  const orgsById = useMemo(() => {
    const m: Record<string, Org> = {}
    for (const o of orgs) m[o.id] = o
    return m
  }, [orgs])

  const selected = selectedId ? orgsById[selectedId] : null

  // Sync form whenever the selection changes (drop dirty edits silently;
  // the page warns the user before they switch if they have unsaved work).
  useEffect(() => {
    if (creating) return
    if (selected) {
      setForm(orgToForm(selected))
      setDirty(false)
    } else {
      setForm(FRESH_FORM)
      setDirty(false)
    }
  }, [selectedId, creating]) // eslint-disable-line

  const startCreate = () => {
    if (dirty && !confirm('Discard unsaved edits?')) return
    setCreating(true)
    setForm({ ...FRESH_FORM })
    setDirty(true)
    setMessage('')
  }

  const cancelCreate = () => {
    setCreating(false)
    if (selected) setForm(orgToForm(selected))
    setDirty(false)
  }

  const onSelect = (id: string) => {
    if (dirty && !confirm('Discard unsaved edits?')) return
    setCreating(false)
    setSelectedId(id)
  }

  const toggleCollapsed = (id: string) => {
    const next = new Set(collapsed)
    if (next.has(id)) next.delete(id); else next.add(id)
    setCollapsed(next)
  }

  const updateForm = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm(f => ({ ...f, [k]: v }))
    setDirty(true)
  }

  const submit = async () => {
    let settings: any = {}
    try { settings = JSON.parse(form.settings_json || '{}') } catch {
      setMessage('settings is not valid JSON')
      return
    }
    if (!form.name.trim()) {
      setMessage('name is required')
      return
    }

    const payload: any = {
      name:            form.name.trim(),
      kind:            form.kind,
      jurisdiction:    form.jurisdiction.trim() || null,
      regulatory_zone: form.regulatory_zone.trim() || null,
      data_residency:  form.data_residency,
      isolation_mode:  form.isolation_mode,
      settings,
    }

    setMessage('Saving...')
    try {
      if (creating) {
        // New child of the selected node (or root if no selection AND kind=group).
        if (selectedId) payload.parent_id = selectedId
        else payload.parent_id = null
        const { data } = await api.post('/api/cdm/orgs', payload)
        setMessage(`Created ${data.org?.name}`)
        setCreating(false)
        setSelectedId(data.org?.id)
        setDirty(false)
        load()
      } else if (selectedId) {
        const { data } = await api.patch(`/api/cdm/orgs/${selectedId}`, payload)
        setMessage(`Updated ${data.org?.name}`)
        setDirty(false)
        load()
      }
    } catch (e: any) {
      setMessage(`Save failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const onDelete = async () => {
    if (!selected) return
    if (!confirm(`Delete org "${selected.name}"? This is permanent.`)) return
    setMessage('Deleting...')
    try {
      await api.delete(`/api/cdm/orgs/${selected.id}`)
      setMessage(`Deleted ${selected.name}`)
      setSelectedId(null)
      load()
    } catch (e: any) {
      const detail = e.response?.data?.detail
      if (typeof detail === 'object' && detail) {
        setMessage(
          `Cannot delete: ${detail.child_orgs} child org(s), ` +
          `${detail.cdm_events} event row(s). ${detail.hint || ''}`
        )
      } else {
        setMessage(`Delete failed: ${detail || e.message}`)
      }
    }
  }

  const startReparent = () => {
    if (!selected) return
    setReparentMode(true)
    setReparentSourceId(selected.id)
    setMessage(`Click the new parent for "${selected.name}"... (click the source again to cancel)`)
  }

  const cancelReparent = () => {
    setReparentMode(false)
    setReparentSourceId(null)
    setMessage('')
  }

  const onReparentTarget = async (targetId: string | null) => {
    if (!reparentSourceId || !targetId) return cancelReparent()
    if (targetId === reparentSourceId) return cancelReparent()
    const src = orgsById[reparentSourceId]
    const dst = orgsById[targetId]
    if (!src || !dst) return cancelReparent()
    if (!confirm(`Reparent "${src.name}" to "${dst.name}"?`)) return cancelReparent()
    try {
      await api.patch(`/api/cdm/orgs/${reparentSourceId}`, { parent_id: targetId })
      setMessage(`Moved "${src.name}" under "${dst.name}"`)
      cancelReparent()
      load()
    } catch (e: any) {
      setMessage(`Reparent failed: ${e.response?.data?.detail || e.message}`)
      cancelReparent()
    }
  }

  const startRingFence = () => {
    if (!selected) return
    setFencing(true)
    setFenceConfirm('')
  }

  const submitRingFence = async () => {
    if (!selected) return
    if (fenceConfirm !== selected.name) {
      setMessage('Confirmation must match the org name exactly.')
      return
    }
    setMessage('Ring-fencing...')
    try {
      const { data } = await api.post(`/api/cdm/orgs/${selected.id}/ring-fence`, {
        confirm: fenceConfirm,
      })
      setMessage(
        `Ring-fenced "${selected.name}". ${data.subtree_size} node(s) flipped, ` +
        `${data.revoked_grants?.length || 0} cascade grant(s) revoked.`
      )
      setFencing(false)
      setFenceConfirm('')
      load()
    } catch (e: any) {
      setMessage(`Ring-fence failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const showSettingsField = form.settings_json !== '{}' || dirty

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/admin" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Admin
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <GitBranch size={18} className="text-cyan" /> ORG TREE
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          hierarchy · isolation · jurisdiction · ring-fence
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button type="button" onClick={startCreate} className="btn-primary">
            <Plus size={11} /> {selectedId ? 'New child org' : 'New root group'}
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: tree */}
        <div className="card p-3">
          <div className="flex items-center gap-2 mb-2">
            <GitBranch size={12} className="text-text-muted" />
            <span className="text-[10px] uppercase tracking-widest text-text-muted">
              Hierarchy ({orgs.length})
            </span>
            {reparentMode && (
              <button type="button" onClick={cancelReparent}
                      className="ml-auto text-[10px] text-amber-light hover:underline">
                cancel reparent
              </button>
            )}
          </div>
          {tree.length === 0 && !loading && (
            <div className="text-xs text-text-muted py-6 text-center">
              No orgs yet. Use 'New root group' to create the first node.
            </div>
          )}
          <div className="space-y-0">
            {tree.map(node => (
              <TreeRow
                key={node.id}
                node={node}
                selectedId={selectedId}
                collapsed={collapsed}
                onSelect={onSelect}
                onToggle={toggleCollapsed}
                reparentMode={reparentMode}
                reparentSourceId={reparentSourceId}
                onReparentTarget={onReparentTarget}
              />
            ))}
          </div>
        </div>

        {/* Right: detail / form */}
        <div className="card p-3">
          {!selected && !creating && (
            <div className="text-xs text-text-muted py-6 text-center">
              Select an org on the left, or click 'New root group' to add one.
            </div>
          )}

          {(selected || creating) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {creating ? (
                  <>
                    <Plus size={14} className="text-emerald-light" />
                    <h2 className="text-sm font-semibold text-text-primary">
                      New {selectedId ? 'child' : 'root'} org
                      {selectedId && (
                        <span className="text-text-muted ml-2 font-normal">
                          under {orgsById[selectedId]?.name}
                        </span>
                      )}
                    </h2>
                  </>
                ) : (
                  <>
                    <Building2 size={14} className="text-cyan-light" />
                    <h2 className="text-sm font-semibold text-text-primary">
                      Edit {selected?.name}
                    </h2>
                    {selected?.isolation_mode === 'ring_fenced' && (
                      <span className="text-[10px] uppercase tracking-widest text-rose-light flex items-center gap-1">
                        <Lock size={10} /> ring-fenced
                      </span>
                    )}
                  </>
                )}
                {creating && (
                  <button type="button" onClick={cancelCreate}
                          className="ml-auto text-text-muted hover:text-text-primary">
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="space-y-2">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-text-muted">Name</span>
                  <input
                    type="text" value={form.name}
                    onChange={e => updateForm('name', e.target.value)}
                    className="input w-full text-xs"
                    placeholder="ACME Holdings PLC"
                  />
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-text-muted">Kind</span>
                    <select value={form.kind}
                            onChange={e => updateForm('kind', e.target.value)}
                            className="input w-full text-xs">
                      {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-text-muted">Isolation mode</span>
                    <select value={form.isolation_mode}
                            onChange={e => updateForm('isolation_mode', e.target.value)}
                            className="input w-full text-xs">
                      {ISOLATION_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </label>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-text-muted">Jurisdiction</span>
                    <input
                      type="text" value={form.jurisdiction}
                      onChange={e => updateForm('jurisdiction', e.target.value.toUpperCase())}
                      maxLength={8}
                      className="input w-full text-xs font-mono"
                      placeholder="GB"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-text-muted">Regulatory zone</span>
                    <input
                      type="text" value={form.regulatory_zone}
                      onChange={e => updateForm('regulatory_zone', e.target.value)}
                      maxLength={20}
                      className="input w-full text-xs font-mono"
                      placeholder="PRA"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-text-muted">Data residency</span>
                    <select value={form.data_residency}
                            onChange={e => updateForm('data_residency', e.target.value)}
                            className="input w-full text-xs">
                      {RESIDENCIES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </label>
                </div>

                {showSettingsField && (
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-text-muted">
                      Settings (JSON)
                    </span>
                    <textarea
                      value={form.settings_json}
                      onChange={e => updateForm('settings_json', e.target.value)}
                      rows={4}
                      spellCheck={false}
                      className="w-full font-mono text-[11px] bg-surface-raised p-2 rounded border border-border"
                    />
                  </label>
                )}
              </div>

              {/* Save / cancel */}
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <button type="button" onClick={submit}
                        disabled={!dirty}
                        className={clsx('btn-primary', !dirty && 'opacity-50 cursor-not-allowed')}>
                  <Save size={11} /> {creating ? 'Create' : 'Save changes'}
                </button>
                {!creating && selected && (
                  <>
                    <button type="button" onClick={startReparent}
                            disabled={reparentMode}
                            className="btn-ghost">
                      <Move size={11} /> Reparent
                    </button>
                    <button type="button" onClick={onDelete}
                            className="btn-ghost text-rose-light">
                      <Trash2 size={11} /> Delete
                    </button>
                    <div className="ml-auto" />
                    {selected.isolation_mode !== 'ring_fenced' && (
                      <button type="button" onClick={startRingFence}
                              className="px-2 py-1 text-[11px] rounded border border-rose/40 bg-rose/10 text-rose-light hover:bg-rose/20 inline-flex items-center gap-1">
                        <ShieldOff size={11} /> Ring-fence...
                      </button>
                    )}
                  </>
                )}
              </div>

              {!creating && selected && (
                <div className="text-[10px] text-text-muted font-mono pt-2 border-t border-border space-y-0.5">
                  <div>id: {selected.id}</div>
                  <div>parent: {selected.parent_id || '(root)'}</div>
                  <div>created: {selected.created_at?.slice(0, 19) || '—'}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Ring-fence confirm modal */}
      {fencing && selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card p-4 w-full max-w-lg space-y-3 border-rose/40">
            <div className="flex items-center gap-2 text-rose-light">
              <AlertTriangle size={16} />
              <h2 className="text-sm font-semibold">Ring-fence "{selected.name}"</h2>
              <button type="button" onClick={() => { setFencing(false); setFenceConfirm('') }}
                      className="ml-auto text-text-muted hover:text-text-primary">
                <X size={14} />
              </button>
            </div>
            <div className="text-xs text-text-secondary space-y-2">
              <p>This will:</p>
              <ul className="list-disc pl-5 space-y-1 text-[11px]">
                <li>Set <code className="text-rose-light">isolation_mode='ring_fenced'</code> on this org and every descendant.</li>
                <li>Revoke EVERY active <code>home_org_cascade</code> and <code>global_view</code> role grant — even ones unrelated to this subtree (cascade grants don't filter, so we revoke all of them defensively).</li>
                <li>Force this node's <code>kind</code> to <code>ring_fenced</code>.</li>
              </ul>
              <p className="text-rose-light">
                Affected users will lose access until you re-issue narrower explicit_grants.
              </p>
              <p>Type the org name <code className="font-mono text-text-primary">{selected.name}</code> to confirm:</p>
            </div>
            <input
              type="text" value={fenceConfirm}
              onChange={e => setFenceConfirm(e.target.value)}
              autoFocus
              className="input w-full text-xs font-mono"
              placeholder={selected.name}
            />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setFencing(false); setFenceConfirm('') }}
                      className="btn-ghost">Cancel</button>
              <button type="button" onClick={submitRingFence}
                      disabled={fenceConfirm !== selected.name}
                      className={clsx(
                        'px-3 py-1.5 text-xs rounded inline-flex items-center gap-1',
                        fenceConfirm === selected.name
                          ? 'bg-rose text-white hover:bg-rose/80'
                          : 'bg-rose/30 text-text-muted cursor-not-allowed',
                      )}>
                <Lock size={11} /> Ring-fence
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-[10px] text-text-muted text-center">
        cdm_orgs schema lives since Phase 1; this admin surface lets you finally curate the hierarchy. Ring-fencing is destructive — every cascade grant is revoked and only narrow explicit grants survive.
      </p>
    </div>
  )
}
